/**
 * The words the papers screen uses for what the app read — in one place, and
 * pinned by `reading.test.ts`, like `lib/period.ts` for periods.
 *
 * Nothing here decides anything. The state, the kind, the place (*Article 2*)
 * and the labels all come off the API; this only turns them into the one
 * sentence an officer reads beside a paper.
 */

import type { RecordPaperKind, ReadingView } from '../../api/client'

/** Reading states in which the worker is still on it, so the screen polls. */
export const READING_LIVE = new Set(['QUEUED', 'READING'])

/** How a reading held back for the officer's word begins its reason (Lane R):
 * filed as one kind, its text speaks of a finding or a penalty. */
export const HELD = 'Not read:'

/** The reason a paper's reading is held until the officer says what it is, or
 * null. Said beside the kind picker, where the act is — not as a failure. */
export function heldReason(latest: ReadingView | null | undefined): string | null {
  if (!latest || latest.state !== 'FAILED') return null
  const error = (latest.error ?? '').trim()
  return error.startsWith(HELD) ? error : null
}

/** Where a paper's reading has got to, in one sentence — or null when the
 * reading finished and there is nothing to say beyond what it read, or when it
 * is held for the officer's word (the kind line says that). */
export function readingSentence(latest: ReadingView | null | undefined): string | null {
  if (!latest) return null
  if (heldReason(latest)) return null
  switch (latest.state) {
    case 'QUEUED':
      return 'Waiting to be read for what it says.'
    case 'READING':
      return 'Being read for what it says.'
    case 'FAILED':
      return `Could not be read: ${latest.error ?? 'the reading service did not say why.'}`
    case 'NOTHING_FOUND':
      return 'Read — nothing it says could be checked against its pages.'
    default:
      return null
  }
}

/** Where on the paper an entry is. A Word file has no pages to name. */
export function pageWords(
  pageFirst: number | null | undefined,
  pageLast: number | null | undefined,
  textOrigin: string | null | undefined,
): string {
  if (textOrigin === 'DOCX_UPLOAD') return 'in the Word file'
  if (!pageFirst) return 'no page'
  if (!pageLast || pageLast === pageFirst) return `p. ${pageFirst}`
  return `pp. ${pageFirst}–${pageLast}`
}

/** A kind key as the officer reads it: the English, then the office's own name. */
export function kindWords(kinds: RecordPaperKind[], key: string | null | undefined): string {
  if (!key) return ''
  const found = kinds.find((kind) => kind.key === key)
  return found ? `${found.label_en} (${found.label_hi})` : key
}

/** What a reading was asked as, and on whose word. When the app guessed the
 * kind and an officer has since named the same kind without it being read
 * again — nothing about the reading would change — the line says so, rather
 * than go on calling the kind a guess. */
export function readAsWords(
  kinds: RecordPaperKind[],
  reading: ReadingView,
  officerKind: string | null | undefined,
): string {
  const kind = kindWords(kinds, reading.kind)
  if (reading.kind_confirmed) return `Read as ${kind}`
  if (officerKind && officerKind === reading.kind) {
    return `Read as ${kind}, on the app’s guess of its kind, which an officer has since confirmed`
  }
  return `Read as ${kind}, on the app’s guess of its kind`
}

const WITHHELD: Record<string, string> = {
  statement: 'the line saying what it says',
  topic: 'the topic describing it',
  kind: 'the label naming its outcome',
  name: 'the app’s own name for it',
  value: 'a value that speaks of a finding or a penalty',
  quote: 'words of it that speak of a finding or a penalty',
}

/** What the record holds back from a paper kept in its authors' own words,
 * in one sentence — so a field that is empty says why (rule 3). The server
 * names what it withheld; this only puts it into words. */
export function withheldWords(withheld: unknown): string | null {
  if (!Array.isArray(withheld) || withheld.length === 0) return null
  const parts = withheld.map((key) => WITHHELD[String(key)] ?? String(key))
  const listed =
    parts.length === 1
      ? parts[0]
      : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`
  return `Not shown, because this paper is kept in its author’s own words: ${listed}.`
}

/** Said under an entry whose words the app completed to their sentence. */
export const KEPT_WHOLE =
  'Kept as the whole sentence these words stand in, so that nothing beside them is left out.'
